import "./LoggedOutForm.scss";

import { History } from "history";
import * as React from "react";
import { connect } from "react-redux";

import TextField from "@material-ui/core/TextField";

import { getRequestProgress } from "src/requestProgress/selector";
import QuiqButton from "src/shared/QuiqButton";
import { SUPPORT_EMAIL } from "../constants";
import { actions, RequestProgressState } from "../requestProgress/reducer";

import classnames from "classnames";
import { isDbsJwtLoginEnabled } from "src/configState/selector";
import { isRequesting } from "../requestProgress/helpers";
import { loadRedirectUrl } from "../route/LocalStorage";
import { isUserAuthenticated } from "../route/selector";
import selectorsToProps from "../utils/selectorsToProps";
import { login, LoginActionType, resetUser } from "./actions";
import LoggedOutView from "./LoggedOutView";
import { getLoginErrorMessage } from "./selector";

interface LoginRequiredComponentProps {
  loginRequiredComponentProps: {
    onClickLoginButton: (event: any) => void;
  };
}

const LoginRequiredComponent: React.SFC<LoginRequiredComponentProps> = ({
  loginRequiredComponentProps
}) => (
  <div>
    <div className="LoggedOutForm__field">
      You are trying to access a page that requires credentials to access.
      Please log in to your account to continue.
    </div>
    <div className="LoggedOutForm__button-row">
      <QuiqButton
        onClick={loginRequiredComponentProps.onClickLoginButton}
        className="LoginButton"
        color="blue"
      >
        LOG IN
      </QuiqButton>
    </div>
  </div>
);

interface LoginFormComponentProps {
  loginFormComponentProps: {
    errorMessage: string;
    username: string;
    password: string;
    formOnChange: (event: any) => void;
    formSubmit: (event: any) => void;
  };
  onChangeLoginType?: () => void;
}

const LoginFormComponent: React.SFC<LoginFormComponentProps> = ({
  loginFormComponentProps
}) => {
  const isLoading = isRequesting(actions.login);

  return (
    <form onSubmit={loginFormComponentProps.formSubmit}>
      <div className="LoggedOutForm__field">
        <TextField
          type="text"
          value={loginFormComponentProps.username}
          onChange={loginFormComponentProps.formOnChange}
          name="username"
          id="username"
          variant="outlined"
          label="Email"
          helperText={"Required *"}
          error={loginFormComponentProps.errorMessage.length > 0}
          disabled={isLoading}
          className={classnames("LoggedOutForm__field__text-input", {
            "LoggedOutForm__field__text-input--error":
              loginFormComponentProps.errorMessage.length > 0
          })}
        />
      </div>
      <div className="LoggedOutForm__field">
        <TextField
          type="password"
          value={loginFormComponentProps.password}
          onChange={loginFormComponentProps.formOnChange}
          name="password"
          id="password"
          variant="outlined"
          label="Password"
          error={loginFormComponentProps.errorMessage.length > 0}
          helperText={loginFormComponentProps.errorMessage}
          disabled={isLoading}
          className={classnames("LoggedOutForm__field__text-input", {
            "LoggedOutForm__field__text-input--error":
              loginFormComponentProps.errorMessage.length > 0
          })}
        />
      </div>
      <div className="LoggedOutForm__button-row">
        <QuiqButton
          type="submit"
          color="blue"
          isLoading={isLoading}
          className="LoginButton"
        >
          LOG IN
        </QuiqButton>
      </div>
    </form>
  );
};

const DBSLoginFormComponent: React.SFC<LoginFormComponentProps> = ({
  loginFormComponentProps,
  onChangeLoginType
}) => {
  const isLoading = isRequesting(actions.login);

  return (
    <form onSubmit={loginFormComponentProps.formSubmit}>
      <div className="LoggedOutForm__field">
        <TextField
          type="text"
          value={loginFormComponentProps.username}
          onChange={loginFormComponentProps.formOnChange}
          name="username"
          id="username"
          variant="outlined"
          label="JWT Token"
          helperText={
            (loginFormComponentProps.errorMessage && "Something went wrong.") ||
            "Required *"
          }
          error={loginFormComponentProps.errorMessage.length > 0}
          disabled={isLoading}
          className={classnames("LoggedOutForm__field__text-input", {
            "LoggedOutForm__field__text-input--error":
              loginFormComponentProps.errorMessage.length > 0
          })}
        />
      </div>
      <div className="LoggedOutForm__button-row">
        <QuiqButton
          type="submit"
          color="blue"
          isLoading={isLoading}
          className="LoginButton"
        >
          LOG IN
        </QuiqButton>
      </div>
    </form>
  );
};

interface LoginProps {
  history: History;
  login: LoginActionType;
  requestProgress: RequestProgressState;
  errorMessage: string;
  isFirstLoginScreen: boolean;
  resetUser: typeof resetUser;
  isUserAuthenticated: boolean;
  isDbsJwtLoginEnabled: boolean;
}

interface LoginState {
  username: string;
  password: string;
  isFirstLoginScreen: boolean;
  redirectUrl: string;
  isDbsJwtLogin: boolean;
}

export class Login extends React.Component<LoginProps, LoginState> {
  constructor(props: LoginProps) {
    super(props);
    const redirectPath = loadRedirectUrl();

    this.state = {
      username: "",
      password: "",
      isFirstLoginScreen: Boolean(redirectPath),
      redirectUrl: redirectPath || "/spreads",
      isDbsJwtLogin: props.isDbsJwtLoginEnabled
    };
  }

  public handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      [event.target.name]: event.target.value
    } as any);
  };

  public goToRedirectUrl() {
    this.props.history.push("/spreads");
  }

  public componentDidMount() {
    const jwt = new URLSearchParams(this.props.history.location.search).get(
      "jwt"
    );
    if (jwt && this.props.isDbsJwtLoginEnabled) {
      this.setState(
        {
          username: jwt,
          isFirstLoginScreen: false
        },
        () => this.handleSubmit()
      );
    } else if (this.props.isUserAuthenticated) {
      // tslint:disable-next-line: no-console
      console.log(
        "GoToRedirectUrl ------------------------------------------Login"
      );
      this.goToRedirectUrl();
    }
  }

  public componentWillReceiveProps(nextProps: LoginProps) {
    if (nextProps.isUserAuthenticated) {
      this.goToRedirectUrl();
    }
  }

  public changeLoginType = () => {
    this.setState({ isDbsJwtLogin: !this.state.isDbsJwtLogin });
  };

  public handleSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
    const { username, password, isDbsJwtLogin } = this.state;

    this.props.resetUser({ errorMessage: "" });
    this.props.login({ username, password, isDbsJwtLogin });
    if (event) {
      event.preventDefault();
    }
  };

  public renderHeader = () => {
    if (this.state.isFirstLoginScreen) {
      return "Login Required";
    }
    return "Log In";
  };

  public renderPrompt = () => {
    if (this.state.isFirstLoginScreen) {
      return "";
    }
    if (this.state.isDbsJwtLogin) {
      return "Please enter your DBS JWT token.";
    }
    return "Please enter your email and password.";
  };

  public renderContent = () => {
    if (this.state.isFirstLoginScreen) {
      const loginRequiredComponentProps = {
        onClickLoginButton: this.displayLoginForm
      };
      return (
        <LoginRequiredComponent
          loginRequiredComponentProps={loginRequiredComponentProps}
        />
      );
    }

    const loginFormComponentProps = {
      username: this.state.username,
      password: this.state.password,
      requestProgress: this.props.requestProgress,
      errorMessage: this.props.errorMessage || "",
      formOnChange: this.handleChange,
      formSubmit: this.handleSubmit
    };
    if (this.state.isDbsJwtLogin) {
      return (
        <DBSLoginFormComponent
          loginFormComponentProps={loginFormComponentProps}
          onChangeLoginType={this.changeLoginType}
        />
      );
    }
    return (
      <LoginFormComponent loginFormComponentProps={loginFormComponentProps} />
    );
  };

  public displayLoginForm = () => {
    this.setState({
      isFirstLoginScreen: false
    });
  };

  public render() {
    return (
      <div>
        <LoggedOutView>
          {{
            header: this.renderHeader(),
            prompt: this.renderPrompt(),
            content: this.renderContent(),
            footer: (
              <div>
                {!this.state.isFirstLoginScreen &&
                  this.props.isDbsJwtLoginEnabled && (
                    <div className="LoggedOutForm__switch_login-row">
                      {/*<QuiqButton
                        type="button"
                        color="white"
                        onClick={this.changeLoginType}
                      >
                        Switch to
                        {this.state.isDbsJwtLogin ? " password " : " JWT "}
                        login
                      </QuiqButton>*/}
                    </div>
                  )}
                <div>Need help?</div>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="Help__menu__link"
                >
                  {SUPPORT_EMAIL}
                </a>
              </div>
            )
          }}
        </LoggedOutView>
      </div>
    );
  }
}

export default connect(
  selectorsToProps({
    requestProgress: getRequestProgress,
    isUserAuthenticated,
    errorMessage: getLoginErrorMessage,
    isDbsJwtLoginEnabled
  }),
  {
    login: login.action,
    resetUser
  }
)(Login);



// WEBPACK FOOTER //
// ./src/login/Login.tsx